/**
 * Health Monitor
 * Extracted from WebSocketSingleton.ts
 * Monitors WebSocket connection health with periodic checks
 */

import { log } from "@/shared/utils/logger";

export interface HealthCheckCallbacks {
  onHealthCheck: () => void;
  onConnectionLost: () => void;
  isConnected: () => boolean;
}

export class HealthMonitor {
  private healthCheckTimer: NodeJS.Timeout | null = null;
  private healthCheckInterval: number;
  private callbacks: HealthCheckCallbacks | null = null;

  constructor(healthCheckInterval: number = 5000) {
    this.healthCheckInterval = healthCheckInterval;
  }

  /**
   * Start health monitoring with periodic checks
   */
  start(callbacks: HealthCheckCallbacks): void {
    // Clear any existing timer
    this.stop();

    this.callbacks = callbacks;

    // Check health at specified interval
    this.healthCheckTimer = setInterval(() => {
      if (this.callbacks) {
        if (this.callbacks.isConnected()) {
          // Connection is healthy, perform health check
          try {
            this.callbacks.onHealthCheck();
          } catch (error) {
            log.warn(
              "HealthMonitor",
              "Health check failed:",
              error
            );
            // Notify connection lost
            this.callbacks.onConnectionLost();
          }
        } else {
          // Connection lost, attempt to restore
          log.debug(
            "HealthMonitor",
            "Connection lost during health check"
          );
          this.callbacks.onConnectionLost();
        }
      }
    }, this.healthCheckInterval);

    log.debug(
      "HealthMonitor",
      `Health monitoring started (interval: ${this.healthCheckInterval}ms)`
    );
  }

  /**
   * Stop health monitoring
   */
  stop(): void {
    if (this.healthCheckTimer) {
      clearInterval(this.healthCheckTimer);
      this.healthCheckTimer = null;
      log.debug("HealthMonitor", "Health monitoring stopped");
    }
  }

  /**
   * Check if health monitoring is active
   */
  isActive(): boolean {
    return this.healthCheckTimer !== null;
  }

  /**
   * Update health check interval
   * Restarts monitoring if currently active
   */
  setInterval(intervalMs: number): void {
    const wasActive = this.isActive();
    this.healthCheckInterval = intervalMs;

    if (wasActive && this.callbacks) {
      this.start(this.callbacks);
    }
  }

  /**
   * Get current health check interval
   */
  getInterval(): number {
    return this.healthCheckInterval;
  }
}
